import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
} from "@mui/material";
import { useState } from "react";
import { useQuery, useQueryClient } from "react-query";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import {
  listHeldDeliveries,
  listOperationalControls,
  OperationalControl,
  releaseOperationalControl,
  resolveHeldDelivery,
} from "../../api/operationalControls";
import {
  Box,
  ButtonPrimary,
  ButtonPrimary1,
  ButtonSize,
  HStack,
  VStack,
} from "../../components/utils";
import { useCompanyStore } from "../../store";
import { displayOperationalControlValue } from "./display";

const controlDescription = (control: OperationalControl) =>
  `Access: ${displayOperationalControlValue(
    control.policy.incomingAccess || "ALLOW"
  )}. Communications: ${displayOperationalControlValue(
    control.policy.outboundCommunication || "ALLOW"
  )}. Automation: ${displayOperationalControlValue(
    control.policy.automation || "ALLOW"
  )}.`;

export default function OperationalControls() {
  const companyId = useCompanyStore(
    (state) =>
      state.companyData?.id || sessionStorage.getItem("companyId") || ""
  );
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [releaseControl, setReleaseControl] =
    useState<OperationalControl | null>(null);
  const [saving, setSaving] = useState(false);
  const controls = useQuery(
    ["operational-controls", companyId],
    () => listOperationalControls(companyId),
    { enabled: Boolean(companyId) }
  );
  const held = useQuery(
    ["held-deliveries", companyId],
    () => listHeldDeliveries(companyId),
    { enabled: Boolean(companyId) }
  );
  const refresh = () => {
    queryClient.invalidateQueries(["operational-controls", companyId]);
    queryClient.invalidateQueries(["held-deliveries", companyId]);
  };
  const release = async () => {
    if (!releaseControl) return;
    setSaving(true);
    try {
      await releaseOperationalControl(
        companyId,
        releaseControl.id,
        releaseControl.revision
      );
      setReleaseControl(null);
      toast.success("Operational control released");
      refresh();
    } catch {
      toast.error("We couldn't release the operational control");
    } finally {
      setSaving(false);
    }
  };
  const resolveDelivery = async (
    deliveryId: string,
    action: "RELEASE" | "DISCARD" | "RETAIN",
    revision: string | number
  ) => {
    try {
      await resolveHeldDelivery(companyId, deliveryId, action, revision);
      toast.success(
        action === "DISCARD"
          ? "Held delivery discarded"
          : action === "RETAIN"
          ? "Held delivery retained for review"
          : "Held delivery approved"
      );
      held.refetch();
    } catch {
      toast.error("We couldn't update the held delivery");
    }
  };

  if (!companyId) {
    return (
      <Box className="p-6 text-sm text-secondaryText">
        Choose a company before managing operational controls.
      </Box>
    );
  }

  return (
    <main>
      <VStack className="w-full gap-6">
        <HStack className="w-full flex-wrap items-start justify-between gap-4">
          <VStack className="min-w-0 gap-1">
            <h1 className="text-xl font-semibold leading-7 text-headerColor">
              Operational controls
            </h1>
            <p className="text-sm text-secondaryText">
              Rulezero emergency controls for company access, communication
              delivery, and automation.
            </p>
          </VStack>
          <ButtonPrimary
            buttonSize={ButtonSize.MEDIUM}
            onClick={() => navigate("/options/operational-controls/new")}
          >
            Create new control
          </ButtonPrimary>
        </HStack>
        <ControlsCard
          controls={controls.data}
          loading={controls.isLoading}
          onRelease={setReleaseControl}
          onEdit={(control) =>
            navigate(`/options/operational-controls/${control.id}/edit`)
          }
          onClone={(control) =>
            navigate(
              `/options/operational-controls/new?sourceControlId=${control.id}`
            )
          }
        />
        <HeldDeliveriesCard
          deliveries={held.data}
          loading={held.isLoading}
          onResolve={resolveDelivery}
        />
      </VStack>
      <ReleaseControlDialog
        control={releaseControl}
        saving={saving}
        onClose={() => setReleaseControl(null)}
        onRelease={release}
      />
    </main>
  );
}

function ControlsCard({
  controls,
  loading,
  onRelease,
  onEdit,
  onClone,
}: {
  controls?: OperationalControl[];
  loading: boolean;
  onRelease: (control: OperationalControl) => void;
  onEdit: (control: OperationalControl) => void;
  onClone: (control: OperationalControl) => void;
}) {
  return (
    <Box className="rounded-lg border border-borderColor bg-white p-6">
      <VStack className="gap-4">
        <VStack className="gap-1">
          <h2 className="text-base font-semibold leading-6 text-headerColor">
            Controls
          </h2>
          <p className="text-sm text-secondaryText">
            The strictest active control applies when more than one target
            matches.
          </p>
        </VStack>
        {loading ? (
          <p className="text-sm text-secondaryText">Loading controls...</p>
        ) : controls?.length ? (
          controls.map((control) => (
            <HStack
              key={control.id}
              className="w-full flex-wrap items-center justify-between gap-4 border-b border-borderColor pb-4 last:border-0"
            >
              <VStack className="min-w-0 gap-1">
                <HStack className="items-center gap-2">
                  <p className="text-sm font-semibold text-primaryText">
                    {displayOperationalControlValue(control.reasonCode)}
                  </p>
                  <span className="rounded-sm bg-tertiaryBg px-2 py-1 text-xs font-medium text-primaryText">
                    {displayOperationalControlValue(control.status)}
                  </span>
                </HStack>
                <p className="text-xs text-secondaryText">
                  {controlDescription(control)}
                </p>
              </VStack>
              <HStack className="items-center gap-3">
                <ButtonPrimary1
                  buttonSize={ButtonSize.MEDIUM}
                  onClick={() => onClone(control)}
                >
                  Clone control
                </ButtonPrimary1>
                <ButtonPrimary1
                  buttonSize={ButtonSize.MEDIUM}
                  onClick={() => onEdit(control)}
                >
                  {control.status === "RELEASED"
                    ? "Reactivate control"
                    : "Edit control"}
                </ButtonPrimary1>
                {control.status !== "RELEASED" && (
                  <>
                    <ButtonPrimary1
                      buttonSize={ButtonSize.MEDIUM}
                      onClick={() => onRelease(control)}
                    >
                      Release control
                    </ButtonPrimary1>
                  </>
                )}
              </HStack>
            </HStack>
          ))
        ) : (
          <p className="text-sm text-secondaryText">
            No active or scheduled controls.
          </p>
        )}
      </VStack>
    </Box>
  );
}

function HeldDeliveriesCard({
  deliveries,
  loading,
  onResolve,
}: {
  deliveries?: any[];
  loading: boolean;
  onResolve: (
    id: string,
    action: "RELEASE" | "DISCARD" | "RETAIN",
    revision: string | number
  ) => void;
}) {
  return (
    <Box className="rounded-lg border border-borderColor bg-white p-6">
      <VStack className="gap-4">
        <VStack className="gap-1">
          <h2 className="text-base font-semibold leading-6 text-headerColor">
            Held deliveries
          </h2>
          <p className="text-sm text-secondaryText">
            Review communications held before final delivery.
          </p>
        </VStack>
        {loading ? (
          <p className="text-sm text-secondaryText">
            Loading held deliveries...
          </p>
        ) : deliveries?.length ? (
          deliveries.map((delivery) => (
            <HStack
              key={delivery.id}
              className="w-full flex-wrap items-center justify-between gap-4 border-b border-borderColor pb-4 last:border-0"
            >
              <VStack className="min-w-0 gap-1">
                <p className="text-sm font-semibold text-primaryText">
                  {delivery.deliveryType} to{" "}
                  {delivery.recipient || "No recipient"}
                </p>
                <p className="text-xs text-secondaryText">
                  Held from {delivery.source}
                </p>
              </VStack>
              <HStack className="items-center gap-3">
                <ButtonPrimary1
                  buttonSize={ButtonSize.MEDIUM}
                  onClick={() =>
                    onResolve(delivery.id, "RETAIN", delivery.revision)
                  }
                >
                  Retain
                </ButtonPrimary1>
                <ButtonPrimary1
                  buttonSize={ButtonSize.MEDIUM}
                  onClick={() =>
                    onResolve(delivery.id, "DISCARD", delivery.revision)
                  }
                >
                  Discard
                </ButtonPrimary1>
                <ButtonPrimary
                  buttonSize={ButtonSize.MEDIUM}
                  onClick={() =>
                    onResolve(delivery.id, "RELEASE", delivery.revision)
                  }
                >
                  Approve
                </ButtonPrimary>
              </HStack>
            </HStack>
          ))
        ) : (
          <p className="text-sm text-secondaryText">
            No deliveries are currently held.
          </p>
        )}
      </VStack>
    </Box>
  );
}

function ReleaseControlDialog({
  control,
  saving,
  onClose,
  onRelease,
}: {
  control: OperationalControl | null;
  saving: boolean;
  onClose: () => void;
  onRelease: () => void;
}) {
  return (
    <Dialog
      open={Boolean(control)}
      onClose={() => !saving && onClose()}
      maxWidth="xs"
      fullWidth
    >
      <DialogTitle className="border-b border-borderColor px-6 py-5 text-base font-semibold text-headerColor">
        Release operational control
      </DialogTitle>
      <DialogContent className="px-6 py-5">
        <p className="text-sm text-secondaryText">
          Releasing this control may restore access, communication delivery, or
          automation. Confirm that the incident is resolved.
        </p>
      </DialogContent>
      <DialogActions className="justify-between border-t border-borderColor px-6 py-4">
        <ButtonPrimary1
          buttonSize={ButtonSize.MEDIUM}
          disabled={saving}
          onClick={onClose}
        >
          Cancel
        </ButtonPrimary1>
        <ButtonPrimary
          buttonSize={ButtonSize.MEDIUM}
          loading={saving}
          loadingText="Releasing"
          onClick={onRelease}
        >
          Release control
        </ButtonPrimary>
      </DialogActions>
    </Dialog>
  );
}
